import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton } from "./GlassButtons";
import { GlassInputControl, GlassInputControlLabel, GlassInputGroup, GlassInputGroupTitle, GlassTextInputTransformer } from "./GlassInput";
import { GlassCheckbox } from "./MitGlassComponents";
import { useContext } from "react";
import { MapitStateContext, MapitStateActionType } from "src/states/MapitState";
import { LayerInfo, PropertyDisplaySpec } from "src/common/managers/Types";
import { Localization } from "@viamap/viamap2-common";
import { ManagedWindow } from "src/WindowManager/WindowState";

export function PopupSettingsCustomizer({
  layerId,
  onClose,
  managedWindow
}: {
  layerId: number;
  onClose: () => void;
  managedWindow: ManagedWindow;
}) {
  const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);

  const layerInfo = mapitState.layers[layerId];

  if (!layerInfo) {
    return <div>{Localization.getText("Layer not found")}</div>;
  }

  const getPropertyAnalysis = (propertyName: string) => {
    const columnIndex = layerInfo.analysisResult?.columnNames.findIndex(name => name === propertyName);
    if (columnIndex !== undefined && layerInfo.analysisResult?.columns[columnIndex]) {
      return layerInfo.analysisResult.columns[columnIndex];
    }
    return null;
  };


  const updatePropertyFormatting = (index: number, updates: Partial<PropertyDisplaySpec['display']>) => {
    const updatedProperties = [...(layerInfo.propertiesToDisplay || [])];
    updatedProperties[index] = {
      ...updatedProperties[index],
      display: {
        ...updatedProperties[index].display,
        ...updates
      }
    };

    const updatedLayerInfo = {
      ...layerInfo,
      propertiesToDisplay: updatedProperties
    };

    mapitStateDispatch({
      type: MapitStateActionType.UpdateDataLayer,
      payload: {
        id: layerId,
        layerInfo: updatedLayerInfo
      }
    });


  };

  const updatePropertyAlias = (index: number, newAlias: string) => {
    const updatedProperties = [...(layerInfo.propertiesToDisplay || [])];
    updatedProperties[index] = {
      ...updatedProperties[index],
      display: {
        ...updatedProperties[index].display,
        aliasName: newAlias || undefined
      }
    };

    const updatedLayerInfo = {
      ...layerInfo,
      propertiesToDisplay: updatedProperties
    };

    mapitStateDispatch({
      type: MapitStateActionType.UpdateDataLayer,
      payload: {
        id: layerId,
        layerInfo: updatedLayerInfo
      }
    });
  };

  const updatePropertyTypeOverride = (index: number, typeOverride: string | undefined) => {
    const updatedProperties = [...(layerInfo.propertiesToDisplay || [])];
    updatedProperties[index] = {
      ...updatedProperties[index],
      display: {
        ...updatedProperties[index].display,
        typeOverride: typeOverride as "String" | "Integer" | "Float" | "ImageUrl" | "link" | undefined
      }
    };

    mapitStateDispatch({
      type: MapitStateActionType.UpdateDataLayer,
      payload: {
        id: layerId,
        layerInfo: {
          ...layerInfo,
          propertiesToDisplay: updatedProperties
        }
      }
    });
  };

  return (
    <AdvancedDragModal
      window={managedWindow}
      title={Localization.getText("Popup Settings Customizer")}
      PosDefault={{ width: "400px", top: "100px", left: "20px" }}
      onClose={onClose}
      variant="NSDark"
    >
      <ADModalBody>
        {/* Dataset name as plain text */}
        <div style={{ marginBottom: "20px", padding: "10px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
          <strong>{Localization.getText("Dataset")}:</strong> {layerInfo.datasetname || Localization.getText("Unknown")}
        </div>

        <GlassInputGroup>
          {(layerInfo.propertiesToDisplay || []).map((prop, index) => {
            const analysis = getPropertyAnalysis(prop.name);

            return (
              <PropertyFormattingRow
                key={prop.name}
                propertyName={prop.name}
                dataType={analysis?.dataType || "Unknown"}
                formatting={prop.display || {}}
                onUpdateFormatting={(updates) => updatePropertyFormatting(index, updates)}
                onUpdateAlias={(newAlias) => updatePropertyAlias(index, newAlias)}
                onUpdateTypeOverride={(typeOverride) => updatePropertyTypeOverride(index, typeOverride)}
                layerInfo={layerInfo}
              />
            );
          })}
        </GlassInputGroup>
      </ADModalBody>

      <ADModalFooter>
        <GlassButton onClick={onClose}>
          {Localization.getText("Close")}
        </GlassButton>
      </ADModalFooter>
    </AdvancedDragModal>
  );
}

// Main orchestrator component
function PropertyFormattingRow({
  propertyName,
  dataType,
  formatting,
  onUpdateFormatting,
  onUpdateAlias,
  onUpdateTypeOverride,
  layerInfo
}: {
  propertyName: string;
  dataType: string;
  formatting: PropertyDisplaySpec['display'];
  onUpdateFormatting: (updates: Partial<PropertyDisplaySpec['display']>) => void;
  onUpdateAlias: (newAlias: string) => void;
  onUpdateTypeOverride: (typeOverride: string | undefined) => void;
  layerInfo: LayerInfo;
}) {
  // Helper function to get the column info for a property name
  const getColumnInfo = (propName: string) => {
    return layerInfo.analysisResult?.columns?.find(col =>
      col.name === propName
    );
  };

  const columnInfo = getColumnInfo(propertyName);
  const isHttpColumn = columnInfo?.mitDataType === "Http";
  const isImageColumn = columnInfo?.mitDataType === "ImageUrl";

  // Use overridden type if available, otherwise use the inferred dataType
  // Special case for datasetname - it's always a string but never analyzed
  // Map Integer to Float since they have the same functionality
  // Show actual data types for Http and ImageUrl columns
  const effectiveDataType = formatting?.typeOverride ||
    (propertyName === "datasetname" ? "String" :
      dataType === "Integer" ? "Float" :
        isHttpColumn ? "link" :
          isImageColumn ? "ImageUrl" : dataType);

  // Type override options - match the actual data types used in MapPopup
  const typeOptions = [
    { text: Localization.getText("Unknown"), value: undefined },
    { text: Localization.getText("Text"), value: "String" },
    { text: Localization.getText("Number"), value: "Float" },
    { text: Localization.getText("Image"), value: "ImageUrl" },
    { text: Localization.getText("Link"), value: "link" }
  ];

  return (
    <>
      <GlassInputGroupTitle>
        <div style={{ flex: 1, display: "flex", alignItems: "center", marginTop: "20px", gap: "8px", fontSize: "16px", fontWeight: "bold" }}>

          {propertyName}
          <select
            value={formatting?.typeOverride || effectiveDataType || ""}
            onChange={(e) => onUpdateTypeOverride(e.target.value || undefined)}
            title={Localization.getText("Override Data Type")}
            style={{
              padding: "1px 4px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              backgroundColor: "#fff",
              fontSize: "12px",
              minWidth: "40px",
              flex: 1,
              maxWidth: "80px"
            }}
          >
            {typeOptions.map(option => (
              <option key={option.value || "unknown"} value={option.value || ""}>
                {option.text}
              </option>
            ))}
          </select>
        </div>
      </GlassInputGroupTitle>


      {/* Display Name for non-HTTP columns */}
      {effectiveDataType !== "link" && effectiveDataType !== "ImageUrl" && ((!isHttpColumn && !isImageColumn) || formatting?.typeOverride) && (
        <>
          <GlassInputControlLabel>{Localization.getText("Display Name")}</GlassInputControlLabel>
          <GlassInputControl>
            <input
              type="text"
              value={formatting?.aliasName || ""}
              onChange={(e) => onUpdateAlias(e.target.value)}
              placeholder={propertyName}

            />
          </GlassInputControl>
        </>
      )}

      {/* Formatting options based on effective data type */}
      {effectiveDataType === "Float" || effectiveDataType === "Integer" ? (
        <NumberFormattingComponent
          formatting={formatting}
          onUpdateFormatting={onUpdateFormatting}
        />
      ) : effectiveDataType === "link" || (effectiveDataType === "String" && isHttpColumn && !formatting?.typeOverride) ? (
        <LinkFormattingComponent
          formatting={formatting}
          onUpdateFormatting={onUpdateFormatting}
        />
      ) : effectiveDataType === "ImageUrl" || (effectiveDataType === "String" && isImageColumn && !formatting?.typeOverride) ? (
        <ImageFormattingComponent
          formatting={formatting}
          onUpdateFormatting={onUpdateFormatting}
        />
      ) : effectiveDataType === "String" ? (
        <StringFormattingComponent
          formatting={formatting}
          onUpdateFormatting={onUpdateFormatting}
        />
      ) : (
        <NoFormattingMessage dataType={effectiveDataType} />
      )}
    </>
  );
}


// Number Formatting Component
function NumberFormattingComponent({
  formatting,
  onUpdateFormatting
}: {
  formatting: PropertyDisplaySpec['display'];
  onUpdateFormatting: (updates: Partial<PropertyDisplaySpec['display']>) => void;
}) {
  return (
    <>
      {/* Format Suffix */}
      <GlassInputControlLabel>{Localization.getText("Format Suffix")}</GlassInputControlLabel>
      <GlassInputControl>
        <input
          type="text"
          value={formatting?.numberFormat?.formatSuffix || ""}
          onChange={(e) => onUpdateFormatting({
            numberFormat: {
              ...formatting?.numberFormat,
              formatSuffix: e.target.value || undefined
            }
          })}
          placeholder={Localization.getText("e.g., kr, %, m²")}
          style={{ minWidth: "120px" }}
        />
      </GlassInputControl>

      {/* Decimal Places */}
      <GlassTextInputTransformer label={Localization.getText("Decimal Places")}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%' }}>
          <div style={{
            width: '10%',
            justifyContent: 'flex-start',
            display: 'flex',
            alignItems: 'center',
            marginLeft: '8px'
          }}>
            <GlassCheckbox
              checked={formatting?.numberFormat?.digitNumber !== undefined}
              onChange={(e) => {
                if (e.target.checked) {
                  onUpdateFormatting({
                    numberFormat: {
                      ...formatting?.numberFormat,
                      digitNumber: formatting?.numberFormat?.digitNumber || 2
                    }
                  });
                } else {
                  onUpdateFormatting({
                    numberFormat: {
                      ...formatting?.numberFormat,
                      digitNumber: undefined
                    }
                  });
                }
              }}
            />
          </div>
          {formatting?.numberFormat?.digitNumber !== undefined && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, justifyContent: 'flex-end' }}>
              <label style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>
                {Localization.getText("Count")}
              </label>
              <GlassInputControl>
                <input
                  type="number"
                  min="0"
                  max="10"
                  value={formatting?.numberFormat?.digitNumber || ""}
                  onChange={(e) => onUpdateFormatting({
                    numberFormat: {
                      ...formatting?.numberFormat,
                      digitNumber: parseInt(e.target.value) || 0
                    }
                  })}
                  placeholder="0"
                  style={{ paddingRight: '36px' }}
                />
                <span style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  fontSize: '12px',
                  pointerEvents: 'none'
                }}>
                  {Localization.getText("digits")}
                </span>
              </GlassInputControl>
            </div>
          )}
        </div>
      </GlassTextInputTransformer>

      {/* Thousand Separator */}
      <GlassInputControlLabel>{Localization.getText("Thousand Separator")}</GlassInputControlLabel>
      <div style={{ display: "flex", alignItems: "center", gap: "16px", width: "100%" }}>
        <div style={{
          width: '10%',
          justifyContent: 'flex-start',
          display: 'flex',
          alignItems: 'center',
          marginLeft: '7px'
        }}>
          <GlassCheckbox
            style={{ transform: 'scale(0.8)' }}
            checked={formatting?.numberFormat?.thousandSeparator || false}
            onChange={(e) => onUpdateFormatting({
              numberFormat: {
                ...formatting?.numberFormat,
                thousandSeparator: e.target.checked
              }
            })}
          />
        </div>
      </div>
    </>
  );
}

// Link Formatting Component
function LinkFormattingComponent({
  formatting,
  onUpdateFormatting
}: {
  formatting: PropertyDisplaySpec['display'];
  onUpdateFormatting: (updates: Partial<PropertyDisplaySpec['display']>) => void;
}) {
  return (
    <>
      <GlassInputControlLabel>{Localization.getText("Display Name")}</GlassInputControlLabel>
      <GlassInputControl>
        <input
          type="text"
          placeholder={Localization.getText("Custom link text (leave empty for URL)")}
          value={formatting?.linkFormat?.displayName || ""}
          onChange={(e) => onUpdateFormatting({
            linkFormat: {
              ...formatting?.linkFormat,
              formatType: "url",
              displayName: e.target.value || undefined
            }
          })}
        />
      </GlassInputControl>
    </>
  )
}



// Image Formatting Component
function ImageFormattingComponent({
  formatting,
  onUpdateFormatting
}: {
  formatting: PropertyDisplaySpec['display'];
  onUpdateFormatting: (updates: Partial<PropertyDisplaySpec['display']>) => void;
}) {
  return (
    <>
      <GlassInputControlLabel>{Localization.getText("Image Size")}</GlassInputControlLabel>
      <GlassInputControl>
        <select
          value={formatting?.linkFormat?.imageSize || "Medium"}
          onChange={(e) => onUpdateFormatting({
            linkFormat: {
              ...formatting?.linkFormat,
              formatType: "image",
              imageSize: e.target.value as "Small" | "Medium" | "Large"
            }
          })}
          style={{ minWidth: "120px" }}
        >
          <option value="Small">{Localization.getText("Small")}</option>
          <option value="Medium">{Localization.getText("Medium")}</option>
          <option value="Large">{Localization.getText("Large")}</option>
        </select>
      </GlassInputControl>
    </>
  );
}

// String Formatting Component
function StringFormattingComponent({
  formatting,
  onUpdateFormatting
}: {
  formatting: PropertyDisplaySpec['display'];
  onUpdateFormatting: (updates: Partial<PropertyDisplaySpec['display']>) => void;
}) {
  return (
    <>
      <GlassInputControlLabel>{Localization.getText("Format Type")}</GlassInputControlLabel>
      <GlassInputControl>
        <select
          value={formatting?.stringFormat?.formatType || "none"}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "none") {
              onUpdateFormatting({
                stringFormat: {
                  ...formatting?.stringFormat,
                  formatType: undefined
                }
              });
            } else {
              onUpdateFormatting({
                stringFormat: {
                  ...formatting?.stringFormat,
                  formatType: value as "upper" | "lower" | "title" | "sentence"
                }
              });
            }
          }}
          style={{ minWidth: "120px" }}
        >
          <option value="none">{Localization.getText("None")}</option>
          <option value="upper">{Localization.getText("UPPERCASE")}</option>
          <option value="lower">{Localization.getText("lowercase")}</option>
          <option value="title">{Localization.getText("Title Case")}</option>
          <option value="sentence">{Localization.getText("Sentence case")}</option>
        </select>
      </GlassInputControl>
    </>
  );
}


// No Formatting Message Component
function NoFormattingMessage({ dataType: _a }: { dataType: string }) {
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>

      </div>
      <span style={{ color: "#666", fontStyle: "italic", display: "block", textAlign: "center", fontSize: "12px" }}>
        {Localization.getText("No formatting options found")}
      </span>
    </>
  );
}